import React from 'react';
import { Mail, MapPin, Twitter, Phone, Instagram, Linkedin } from 'lucide-react';

const Footer = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main footer content */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-3 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex flex-col items-center mb-6">
              <img src="/nursebloc-logo.png" alt="NurseBloc Logo" width="150" height="150" className="mb-4 drop-shadow-lg rounded-lg" />
              <span className="text-2xl font-semibold text-white">NurseBloc</span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Virtual nurse support for families and their loved ones. 
              Guidance, coordination, and peace of mind.
            </p>
            <div className="flex gap-4">
              <a href="https://twitter.com/nursebloc" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 hover:bg-violet-600 rounded-lg flex items-center justify-center transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="https://instagram.com/nursebloc" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 hover:bg-violet-600 rounded-lg flex items-center justify-center transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://linkedin.com/company/nursebloc" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 hover:bg-violet-600 rounded-lg flex items-center justify-center transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-4">
              <li>
                <button 
                  onClick={() => scrollToSection('how-it-works')}
                  className="text-slate-400 hover:text-violet-400 transition-colors text-sm"
                >
                  How It Works
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="text-slate-400 hover:text-violet-400 transition-colors text-sm"
                >
                  Our Services
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('why-nurse')}
                  className="text-slate-400 hover:text-violet-400 transition-colors text-sm"
                >
                  Why a Nurse
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('careers')}
                  className="text-slate-400 hover:text-violet-400 transition-colors text-sm"
                >
                  Careers
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white mb-4">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-violet-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-400">Email us at</p>
                  <a href="mailto:info@nursebloc.io" className="text-white hover:text-violet-400 transition-colors">
                    info@nursebloc.io
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-violet-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-400">Based in</p>
                  <span className="text-white">United States</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-800 mt-12 pt-8">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
            <p className="text-sm text-slate-400">
              &copy; 2024 NurseBloc. Support for caregivers and their loved ones. Not medical emergencies. Not pediatric care.
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
              <a href="#" className="hover:text-violet-400 transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-violet-400 transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-violet-400 transition-colors">HIPAA Privacy Practices</a>
              <a href="#" className="hover:text-violet-400 transition-colors">Non-Discrimination</a>
            </div>
          </div>
          
          {/* Legal Compliance Section */}
          <div className="mt-8 pt-6 border-t border-slate-800">
            <div className="text-sm text-slate-400 space-y-3 leading-relaxed">
              <p>
                <strong className="text-slate-300">Notice:</strong> Descriptions of NurseBloc services on this site are provided for general informational purposes only. 
                Availability of specific personnel, care models, and medical procedures may vary by location and in accordance with 
                state legal and regulatory requirements.
              </p>
              <p>
                NurseBloc complies with all applicable federal, state, and local non-discrimination laws and does not discriminate, 
                exclude people, or treat them differently on the basis of race, color, national origin, age, disability, or sex. 
                Where state or local laws provide greater protections, NurseBloc upholds those standards.
              </p>
              <p>
                <strong className="text-slate-300">HIPAA Notice:</strong> NurseBloc is committed to protecting your health information. Our HIPAA Privacy Practices 
                explain how we may use and disclose your protected health information.
              </p>
              <div className="mt-6 pt-4 border-t border-slate-700">
                <p className="text-center">
                  This site is protected by industry-standard security measures. By using this site, you agree to our terms and privacy policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
